// src/services/timInformationService.js
// TI-Messenger-Information API — isInsurance classification
// Implements gematik AFO A_26445-01 (GET /tim-information/v1/server/isInsurance)
// Used by client-side invite filtering (A_26390) to classify the inviter's
// homeserver as belonging to an insurance provider or not.

import matrixService from './matrixService';
import logger from '../utils/logger';
import { getMatrixOpenIdAccessToken } from './openIdToken';
import { abortWithTimeout } from '../utils/abortWithTimeout';
import { timFetch } from '../utils/timUserAgent';

const FETCH_TIMEOUT_MS = 15000;
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

class TimInformationService {
  constructor() {
    this._cache = new Map();    // serverName -> { value: bool, expiresAt: number }
    this._inflight = new Map(); // serverName -> Promise<bool>  (request coalescing)
  }

  /**
   * Async classifier. Returns true/false or throws on failure (caller decides fail-mode).
   * Coalesces concurrent calls for the same serverName into a single request.
   *
   * @param {string} serverName
   * @param {object} [options]
   * @param {AbortSignal} [options.signal] - optional external abort signal for cancellation
   */
  async isInsurance(serverName, options) {
    const cached = this._cache.get(serverName);
    if (cached && Date.now() < cached.expiresAt) {
      return cached.value;
    }

    const existing = this._inflight.get(serverName);
    if (existing) return existing;

    const p = this._fetch(serverName, options).finally(() => {
      this._inflight.delete(serverName);
    });
    this._inflight.set(serverName, p);
    return p;
  }

  /**
   * Synchronous, non-throwing cache peek. Returns the cached boolean if present
   * and unexpired, otherwise null. Used by the notification path so that it can
   * stay synchronous while still benefitting from prior lookups.
   */
  peekCache(serverName) {
    const cached = this._cache.get(serverName);
    if (cached && Date.now() < cached.expiresAt) return cached.value;
    return null;
  }

  clearCache() {
    this._cache.clear();
    this._inflight.clear();
  }

  async _fetch(serverName, { signal } = {}) {
    const client = matrixService.getClient();
    if (!client) throw new Error('Matrix client not ready');

    const baseUrl = client.getHomeserverUrl();
    const openIdToken = await getMatrixOpenIdAccessToken(client, { signal, timeoutMs: FETCH_TIMEOUT_MS });

    const url = `${baseUrl}/tim-information/v1/server/isInsurance?serverName=${encodeURIComponent(serverName)}`;
    const controller = abortWithTimeout(signal, FETCH_TIMEOUT_MS);

    try {
      // The akquinet reference Messenger-Proxy (tim-ref/messenger-proxy) reads the
      // OpenID token from the standard `Authorization: Bearer` header for the
      // /tim-information/* endpoints, not from `X-Matrix-OpenID-Token` (which is
      // what /tim-authenticate expects). Sending both keeps us working against
      // either auth style without risk — the unused header is ignored.
      const res = await timFetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${openIdToken}`,
          'X-Matrix-OpenID-Token': openIdToken,
        },
        signal: controller.signal,
      });

      if (!res.ok) {
        // Deliberately do NOT include the token in the error message.
        throw new Error(`isInsurance HTTP ${res.status}`);
      }

      const body = await res.json();
      const value = !!body?.isInsurance;
      this._cache.set(serverName, {
        value,
        expiresAt: Date.now() + CACHE_TTL_MS,
      });
      return value;
    } catch (err) {
      // Log without including the token.
      logger.warn('[timInformationService] isInsurance lookup failed', {
        serverName,
        message: err?.message,
      });
      throw err;
    } finally {
      controller._clearTimeout();
    }
  }
}

export default new TimInformationService();
